<template>
    <SiteLayout class="mx-auto max-w-full p-0 m-0 relative">
        <div class="fixed z-20 w-full">
            <TopHeader :appSettings="appSettings" />
        </div>

        <div
            class="mt-0 grid grid-colos-1 md:grid-cols-[20rem_1fr] lg:grid-cols-[20rem_1fr] sm:grid-cols-[20rem_1fr] border-transparent border-none p-0 m-0"
        >
            <SiteLeftSideMenu :menuCategories="menuCategories" />

            <div
                class="mt-0 md:mt-[5.4rem] lg:mt-[5.4rem] sm:mt-[5.4rem] flex flex-col bg-white w-full"
            >
                <div>
                    <div
                        class="flex flex-row gap-2 mt-[6rem] md:hidden lg:hidden sm:hidden px-2"
                    >
                        <input
                            type="text"
                            placeholder="Search for products..."
                            v-model="searchTerm"
                            @keydown.enter="searchProduct"
                            class="w-full px-4 py-2 border mx-auto rounded-none focus:outline-none focus:ring focus:ring-blue-300"
                        />
                        <button
                            class="bg-green-600 text-white px-4 py-3 hover:bg-green-700 rounded-md"
                            @click.once="searchProduct"
                        >
                            <i class="pi pi-search"></i>
                        </button>
                    </div>
                    <HeroSection :heroBanners="hero_banners" />
                </div>
                <div
                    class="grid grid-cols-1 gap-0 p-0 mx-auto mt-[0rem] w-full"
                >
                    <CategoryCarousel
                        :categoryName="category?.category_name"
                        v-for="(category, index) in categories"
                        :key="index"
                        :category="category"
                        :app-settings="appSettings"
                        class="px-10"
                    />
                    <div class="flex flex-col gap-3 w-full">
                        <div class="text-left bg-green-600">
                            <h2
                                class="text-xs md:text-md lg:text-md sm:text-sm font-bold p-3 text-white uppercase"
                            >
                                Popular Categories
                            </h2>
                        </div>
                        <Carousel
                            :value="popularCategories"
                            :numVisible="10"
                            :numScroll="1"
                            :responsiveOptions="responsiveOptions"
                            circular
                            :autoplayInterval="0"
                            class="pt-5 rounded-md bg-white px-10"
                        >
                            <template #item="slotProps">
                                <div
                                    class="border-surface-200 dark:border-surface-700 rounded ml-2 p-0 shadow-sm"
                                    v-if="
                                        slotProps.data.category_image !== null
                                    "
                                >
                                    <div class="text-center">
                                        <Link
                                            :href="
                                                route('product.category-view', {
                                                    category_name: slugify(
                                                        slotProps.data.category_name.toLowerCase()
                                                    ),
                                                    id: slotProps.data?.id,
                                                })
                                            "
                                            class="font-bold"
                                        >
                                            <div
                                                class="relative mx-auto img-root-div mb-5 w-[5rem] h-[5rem]"
                                            >
                                                <img
                                                    :src="
                                                        '/storage/' +
                                                        slotProps.data
                                                            .category_image
                                                    "
                                                    :alt="
                                                        slotProps.data
                                                            .category_name
                                                    "
                                                    @error="
                                                        (e) =>
                                                            (e.target.src =
                                                                noImage)
                                                    "
                                                    class="mx-auto rounded"
                                                />
                                            </div>
                                            <span>{{
                                                slotProps.data.category_name
                                            }}</span>
                                        </Link>
                                    </div>
                                </div>
                            </template>
                        </Carousel>
                    </div>

                    <!-- <CategoryCarousel :categoryName="category?.category_name" v-for="(category, index) in categories" :key="index" :category="category" /> -->
                </div>

                <div class="grid grid-cols-1 gap-0 p-0 mx-auto mt-[2rem]">
                    <div class="flex flex-col gap-3 w-full">
                        <div class="text-left bg-green-600">
                            <h2
                                class="p-3 text-xs md:text-md lg:text-md sm:text-sm font-bold uppercase text-white"
                            >
                                Popular Brands
                            </h2>
                        </div>
                        <Carousel
                            :value="indexBrands"
                            :numVisible="10"
                            :numScroll="2"
                            :responsiveOptions="responsiveOptions"
                            circular
                            :autoplayInterval="0"
                            class="pt-5 rounded-md bg-white px-10"
                        >
                            <template #item="slotProps">
                                <div
                                    class="border-surface-200 dark:border-surface-700 rounded ml-2 p-0 shadow-md"
                                    v-if="slotProps.data.brand_logo !== null"
                                >
                                    <div class="mb-4 pb-2 text-center">
                                        <div
                                            class="relative mx-auto img-root-div mb-5 w-[5rem] h-[5rem]"
                                        >
                                            <img
                                                :src="
                                                    '/storage/' +
                                                    slotProps.data.brand_logo
                                                "
                                                :alt="slotProps.data.brand_name"
                                                @error="
                                                    (e) =>
                                                        (e.target.src = noImage)
                                                "
                                                class="mx-auto rounded"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </Carousel>
                    </div>
                </div>
            </div>
        </div>
    </SiteLayout>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { ProductGrid } from "@/Services/ProductGrid";
import TopHeader from "@/Pages/TopHeader.vue";
import HeroSection from "@/Pages/HeroSection.vue";
import CategoryCarousel from "@/Pages/CategoryCarousel.vue";
import CategoryModel from "@/Interfaces/Category/CategoryModel";
import SiteLayout from "@/Layouts/SiteLayout.vue";
import BannerManagementModel from "@/Interfaces/BannerManagement/BannerManagementModel";
import { Link, usePage } from "@inertiajs/vue3";
import BrandModel from "@/Interfaces/Brand/BrandModel";
import { initFlowbite } from "flowbite";
import { useMenuStore } from "@/Stores/MenuStore/MenuStore";
import { storeToRefs } from "pinia";
import slugify from "slugify";
import SiteLeftSideMenu from "@/Pages/SiteLeftSideMenu.vue";
import { useShoppingCartStore } from "@/Stores/ShoppingCart/ShoppingCartStore";
import { useSearchComposable } from "@/Composables/SearchComposable/SearchComposable";
import ApplicationSettingModel from "@/Interfaces/ApplicationSettings/ApplicationSettingModel";

const {
    categories,
    hero_banners,
    popularCategories,
    indexBrands,
    appSettings,
} = defineProps<{
    indexBrands: BrandModel;
    popularCategories: CategoryModel;
    hero_banners: BannerManagementModel;
    categories: CategoryModel;
    appSettings: ApplicationSettingModel;
}>();

const shoppingStore = useShoppingCartStore();
const { cartItems, localStorageCartItems, searchTerm } =
    storeToRefs(shoppingStore);

const logout = usePage().props.flash?.logout;
const noImage = ref<string | null>("/No-Image-Placeholder.svg");

onMounted(() => {
    ProductGrid.getProducts().then(
        (data) => (productsGrid.value = data.slice(0, 20))
    );
    initFlowbite();

    if (logout === true) {
        cartItems.value = [];
        localStorageCartItems.value = [];
        localStorage.removeItem("cartItems");
    }
});

const menuCategories = usePage().props.flash?.menuCategories;

const menuStore = useMenuStore();
const { isMenuOpen } = storeToRefs(menuStore);

const { searchProduct } = useSearchComposable(searchTerm);

const title = ref("");
const productsGrid = ref<Object>({});
const layout = ref<string>("grid");
const options = ref<string[]>(["list", "grid"]);

const responsiveOptions = ref([
    {
        breakpoint: "1400px",
        numVisible: 2,
        numScroll: 1,
    },
    {
        breakpoint: "1199px",
        numVisible: 3,
        numScroll: 1,
    },
    {
        breakpoint: "767px",
        numVisible: 2,
        numScroll: 1,
    },
    {
        breakpoint: "575px",
        numVisible: 2,
        numScroll: 1,
    },
]);

const getSeverity = (status: string) => {
    switch (status) {
        case "INSTOCK":
            return "success";

        case "LOWSTOCK":
            return "warn";

        case "OUTOFSTOCK":
            return "danger";

        default:
            return null;
    }
};

const getGridSeverity = (product: Object) => {
    switch (product?.inventoryStatus) {
        case "INSTOCK":
            return "success";

        case "LOWSTOCK":
            return "warn";

        case "OUTOFSTOCK":
            return "danger";

        default:
            return null;
    }
};

const productForCart = ref();
const addToCart = (item) => {
    productForCart.value = item;
};
</script>
